// Base libs
import { useState } from "react";
import {
    GuxButtonSlot,
    GuxFormFieldRadio,
    GuxModal,
    GuxIcon,
    GuxTooltip,
    GuxInlineAlert,
} from "genesys-spark-components-react";

// General commons
import AutomatedScoringType from "enums/AutomatedScoringType";
import { useStrings } from "features/i18n/useStrings";

// Form editor commons
import { ErrorStatement } from "../common/ErrorStatement/ErrorStatement";

// Feature commons
import {
    selectFormHasMaxAIScoringQuestions,
    selectIsAiScoringAnswerOptionCountValid,
    selectIsQuestionAnswerAutomated,
    selectIsQuestionValidForEA,
} from "features/qualityForm/qualityFormValidations";
import { useAppSelector } from "app/hooks";
import { selectHasAIScoringEditPermission } from "features/qualityForm/qualityFormPermissionSelectors";
import { selectCanUserViewSTATopics } from "features/auth/currentUserSlice";

// Slice commons
import {
    selectAnswerOptionsForQuestion,
    selectIsAutomatedEvaluationForm,
    selectMaxPermissibleQuestionsWithAiScoring,
    selectMaxAiScoringAnswerOptionCount,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";

// Locales
import strings from "./AutomatedScoringSection.strings";

// Locals
import PreviewTag from "../sidePanel/PreviewTag";
import { getAutomatedScoringOptionState } from "util/AutomatedScoringOptionStatus";

// Styles
import "./AutomatedScoringSection.less";

function AutomatedScoringSectionModal({
    isOpened,
    onConfirm,
    onCancel,
    selectedOption,
}: {
    isOpened: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    selectedOption: AutomatedScoringType;
}) {
    const t = useStrings(strings);

    return (
        <GuxModal
            open={isOpened}
            className="AutomatedScoringSectionModal"
            data-testid="automated-scoring-modal"
            onGuxdismiss={(event) => {
                event.preventDefault();
                onCancel();
            }}
        >
            <div
                className="AutomatedScoringSectionModalTitle"
                slot="title"
                data-testid="automated-scoring-modal-title"
            >
                <GuxIcon
                    iconName="fa/hexagon-exclamation-solid"
                    screenreaderText={t.labels.warningForSwitching}
                    decorative={true}
                />
                <span className="gux-heading-lg-bold">
                    {t.labels.modalTitle[selectedOption]}
                </span>
            </div>
            <div
                className="AutomatedScoringSectionModalContent"
                slot="content"
                data-testid="automated-scoring-modal-content"
            >
                {t.labels.modalContent[selectedOption]}
            </div>
            {/* TODO: once spark library update happens, update the button slot names */}
            <div slot="start-align-buttons">
                <GuxButtonSlot accent="primary">
                    <button onClick={onConfirm}>
                        {t.labels.modalFooter.confirm}
                    </button>
                </GuxButtonSlot>
            </div>
            <div slot="end-align-buttons">
                <GuxButtonSlot accent="secondary">
                    <button onClick={onCancel}>
                        {t.labels.modalFooter.cancel}
                    </button>
                </GuxButtonSlot>
            </div>
        </GuxModal>
    );
}

function AutomatedScoringSectionOption({
    questionId,
    option,
    selected,
    disabled,
    tooltipMessage,
    onSelected,
}: {
    questionId: string;
    option: string;
    selected: boolean;
    disabled: boolean;
    tooltipMessage: string;
    onSelected: (option: AutomatedScoringType) => void;
}) {
    const t = useStrings(strings);

    return (
        <span>
            <GuxFormFieldRadio
                className={`AutomatedScoringSectionOption ${
                    selected ? "selected" : ""
                }`}
                aria-disabled={disabled}
            >
                <input
                    slot="input"
                    type="radio"
                    name={`${questionId}-automatedScoring`}
                    value={option}
                    checked={selected}
                    onChange={({ target }) => {
                        onSelected(target.value as AutomatedScoringType);
                    }}
                    disabled={disabled}
                    aria-disabled={disabled}
                />
                <label slot="label" aria-disabled={disabled}>
                    <span>{t.labels.options[option]}</span>
                    {option === AutomatedScoringType.AI_SCORING && (
                        <PreviewTag
                            disabled={disabled}
                            showTooltip={!disabled}
                        />
                    )}
                </label>
            </GuxFormFieldRadio>
            {disabled && <GuxTooltip>{tooltipMessage}</GuxTooltip>}
        </span>
    );
}

export default function AutomatedScoringSection({
    questionId,
    automatedScoringOption,
    onSelected,
}: {
    questionId: string;
    automatedScoringOption?: AutomatedScoringType;
    onSelected: (option: AutomatedScoringType) => void;
}) {
    const t = useStrings(strings);
    const [showModal, setShowModal] = useState(false);
    const modalTypes = [
        AutomatedScoringType.AI_SCORING,
        AutomatedScoringType.EVALUATION_ASSISTANCE,
    ];
    // option state used only for modal (different option state from QuestionEditor)
    const [newSelectedOption, setNewSelectedOption] = useState<
        AutomatedScoringType | undefined
    >(automatedScoringOption);

    const handleConfirm = (option: AutomatedScoringType) => {
        onSelected(option);
        setShowModal(false);
    };

    const handleCancel = () => {
        setNewSelectedOption(automatedScoringOption);
        setShowModal(false);
    };

    const hasAiScoringEditPermission = useAppSelector(
        selectHasAIScoringEditPermission
    );
    const hasTopicViewPermission = useAppSelector(selectCanUserViewSTATopics);

    const tooManyAIScoringQuestions = useAppSelector((state) =>
        selectFormHasMaxAIScoringQuestions(state)
    );

    const maxAIScoringAnswerOptionCount = useAppSelector(
        selectMaxAiScoringAnswerOptionCount
    );

    const tooManyAnswerOptionsForAIScoring = useAppSelector((state) => {
        return (
            (selectAnswerOptionsForQuestion(state, questionId) ?? []).length >
            maxAIScoringAnswerOptionCount
        );
    });

    const isAiScoringAnswerOptionCountInvalid = useAppSelector((state) => {
        return !selectIsAiScoringAnswerOptionCountValid(state, questionId);
    });

    const isQuestionAnswerAutomated = useAppSelector((state) =>
        selectIsQuestionAnswerAutomated(state, questionId)
    );

    const isQuestionHavingEAAndConfiguredConditions = useAppSelector((state) =>
        selectIsQuestionValidForEA(state, questionId)
    );

    const maxAIScoringQuestions = useAppSelector(
        selectMaxPermissibleQuestionsWithAiScoring
    );

    const isAutomatedEvaluationForm = useAppSelector(
        selectIsAutomatedEvaluationForm
    );

    const optionStatus = getAutomatedScoringOptionState(
        automatedScoringOption,
        hasAiScoringEditPermission,
        hasTopicViewPermission,
        tooManyAIScoringQuestions,
        tooManyAnswerOptionsForAIScoring,
        t.labels.tooltip,
        maxAIScoringQuestions,
        maxAIScoringAnswerOptionCount
    );

    const shouldShowAutomationOption = (
        type: AutomatedScoringType,
        isAutomatedEvaluationForm: boolean
    ): boolean => {
        // Hide "No Automation" option for automated evaluation forms
        return !(
            isAutomatedEvaluationForm &&
            type === AutomatedScoringType.NO_AUTOMATION
        );
    };

    return (
        <div
            className="AutomatedScoringSection"
            data-testid="automated-scoring"
        >
            <label className="AutomatedScoringSectionTitle">
                {t.labels.title}
            </label>
            <div
                className="AutomatedScoringSectionOptions"
                key={questionId}
                data-testid="automated-scoring-options"
            >
                {Object.values(AutomatedScoringType)
                    .filter((type) =>
                        shouldShowAutomationOption(
                            type,
                            isAutomatedEvaluationForm
                        )
                    )
                    .map((type) => (
                        <AutomatedScoringSectionOption
                            key={`${questionId}-${type}`}
                            questionId={questionId}
                            option={type}
                            selected={type === automatedScoringOption}
                            disabled={optionStatus[type].disabled}
                            tooltipMessage={optionStatus[type].tooltip}
                            onSelected={(type) => {
                                setNewSelectedOption(type);
                                // if AI Scoring or Evaluation Assistance is selected, then show modal
                                if (
                                    automatedScoringOption &&
                                    modalTypes.includes(automatedScoringOption)
                                ) {
                                    setShowModal(true);
                                } else {
                                    handleConfirm(type);
                                }
                            }}
                        />
                    ))}
            </div>
            {automatedScoringOption === AutomatedScoringType.AI_SCORING ? (
                <GuxInlineAlert accent="info" className="AiScoringOptionLabel">
                    <div slot="content" data-testid="ai-scoring-option-message">
                        {t.labels.aiScoringInfoMessage}
                    </div>
                </GuxInlineAlert>
            ) : (
                <></>
            )}
            {!isQuestionAnswerAutomated && (
                <div
                    className="AutomatedScoringOptionError"
                    data-testid="automated-scoring-option-error"
                >
                    <ErrorStatement
                        errorMessage={
                            t.labels.tooltip.noAutomatedScoringOptionSelected
                        }
                    />
                </div>
            )}
            {!isQuestionHavingEAAndConfiguredConditions && (
                <div
                    className="EvaluationAssistanceConditionRequired"
                    data-testid="evaluation-assistance-condition-required"
                >
                    <ErrorStatement
                        errorMessage={
                            t.labels.tooltip
                                .evaluationAssistanceConditionRequired
                        }
                    />
                </div>
            )}
            {isAiScoringAnswerOptionCountInvalid && (
                <div className="AiScoringOptionError">
                    <ErrorStatement
                        errorMessage={t.labels.tooltip.aiScoringAnswerLimitReached(
                            {
                                maxAIScoringAnswerOptionCount,
                            }
                        )}
                    />
                </div>
            )}
            {showModal && newSelectedOption && (
                <AutomatedScoringSectionModal
                    isOpened={showModal}
                    onConfirm={() => handleConfirm(newSelectedOption)}
                    onCancel={handleCancel}
                    selectedOption={newSelectedOption}
                />
            )}
        </div>
    );
}
